/**
 * ShipStoreDialog — Purchase ship VARIANTS with USD stablecoins.
 *
 * Shows ~40 variants across 10 ship classes with a class filter bar.
 * Falls back to class-based prices if the variants API is unavailable.
 */

import Phaser from 'phaser';
import { ServiceLocator, Services } from '../../utils/ServiceLocator';
import type { MMOMonetizationManager, ShipPriceInfo, ShipVariantInfo } from '../../managers/MMOMonetizationManager';
import type { MMOStateManager } from '../../managers/MMOStateManager';
import type { Chain, Stablecoin } from '../../shared/common/MonetizationTypes';
import { Colors, CSSColors, createDialogChrome, closeDialog, Fonts, FontSize, Radius } from './GameUITheme';
import { EventBus } from '../../utils/EventBus';
import { PaymentSelectionPanel } from './PaymentSelectionPanel';
import { PaymentFlowManager } from '../../managers/PaymentFlowManager';
import { I18n } from '../../i18n/I18nService';
import { isMobile } from '../../utils/ResponsiveHelper';

/* ── Layout constants ────────────────────────────────────── */
const PAD        = 8;
const FILTER_H   = 28;    // height of filter button row
const LIST_TOP   = 55;    // below header chrome

const CHAINS: { id: Chain; label: string }[] = [
  { id: 'solana',    label: 'Solana' },
  { id: 'ethereum',  label: 'Ethereum' },
  { id: 'bsc',       label: 'BSC' },
  { id: 'galachain', label: 'GalaChain' },
];

const CLASS_ORDER = [
  'all', 'scout', 'fighter', 'destroyer', 'bomber', 'space_tug',
  'cruiser', 'carrier', 'battleship', 'dreadnought', 'titan',
] as const;

const CLASS_LABELS: Record<string, string> = {
  all:         'All',
  scout:       'Scout',
  fighter:     'Fighter',
  destroyer:   'Destroyer',
  bomber:      'Bomber',
  space_tug:   'Space Tug',
  cruiser:     'Cruiser',
  carrier:     'Carrier',
  battleship:  'Battleship',
  dreadnought: 'Dreadnought',
  titan:       'Titan',
};

/** Short class/role descriptor for display */
const SHIP_ROLE: Record<string, string> = {
  scout:       'Recon',
  fighter:     'Light Combat',
  destroyer:   'Strike',
  bomber:      'Heavy Strike',
  trade_ship:  'Logistics',
  space_tug:   'Logistics',
  cruiser:     'Heavy Combat',
  carrier:     'Fleet Support',
  battleship:  'Assault',
  dreadnought: 'Capital',
  titan:       'Flagship',
};

/** Map ship class → preloaded sprite texture key */
const SHIP_ICON_KEY: Record<string, string> = {
  scout:        'scout',
  fighter:      'fighter',
  cruiser:      'cruiser',
  bomber:       'bomber',
  carrier:      'carrier',
  battleship:   'battleship',
  destroyer:    'destroyer',
  titan:        'titan',
  dreadnought:  'dreadnaught',
  dreadnaught:  'dreadnaught',
  trade_ship:   'tug',
  space_tug:    'tug',
};

type DetailView = 'info' | 'confirm';

export class ShipStoreDialog extends Phaser.GameObjects.Container {
  private monetization: MMOMonetizationManager;
  private variants: ShipVariantInfo[] = [];
  private filteredVariants: ShipVariantInfo[] = [];
  private selectedVariant: ShipVariantInfo | null = null;
  private activeFilter: string = 'all';
  private detailView: DetailView = 'info';

  /* confirm-flow state */
  private selectedChain: Chain = 'solana';
  private selectedStablecoin: Stablecoin = 'USDC';
  private isPurchasing = false;

  private paymentPanel: PaymentSelectionPanel | null = null;

  /* Responsive sizing */
  private listW: number;
  private detailW: number;
  private scrollH: number;

  /* UI containers */
  private filterContainer!: Phaser.GameObjects.Container;
  private listContainer!: Phaser.GameObjects.Container;
  private listContent!:   Phaser.GameObjects.Container;
  private detailContainer!: Phaser.GameObjects.Container;
  private statusText!: Phaser.GameObjects.Text;
  private scrollY = 0;
  private maxScrollY = 0;

  /* Scrollbar elements */
  private scrollTrack!: Phaser.GameObjects.Graphics;
  private scrollThumb!: Phaser.GameObjects.Graphics;
  private totalContentH = 0;


  constructor(scene: Phaser.Scene) {
    const mobile = isMobile(scene);
    const DIALOG_W = mobile ? 340 : 652;
    const DIALOG_H = mobile ? 540 : 580;
    const LIST_W   = DIALOG_W - PAD * 2;
    const DETAIL_W = DIALOG_W - PAD * 2;
    const SCROLL_H = DIALOG_H - LIST_TOP - FILTER_H - 38;

    const { width, height } = scene.cameras.main;
    super(scene, width / 2 - DIALOG_W / 2, height / 2 - DIALOG_H / 2);

    this.listW = LIST_W;
    this.detailW = DETAIL_W;
    this.scrollH = SCROLL_H;
    this.monetization = ServiceLocator.get<MMOMonetizationManager>(Services.MMO_MONETIZATION);

    createDialogChrome(scene, this, DIALOG_W, DIALOG_H, {
      title: I18n.t('dialog.ship_store'),
      onClose: () => this.close(),
    });

    /* Credits badge (top-right of header, clear of close button) */
    try {
      const mmoState = ServiceLocator.get<MMOStateManager>(Services.MMO_STATE);
      const credits = mmoState.getResources();
      const creditsText = scene.add.text(DIALOG_W - 50, 24, `ISD: ${credits.toLocaleString()}`, {
        fontSize: FontSize.sm, color: CSSColors.gold, fontFamily: Fonts.body,
      }).setOrigin(1, 0.5);
      this.add(creditsText);
    } catch {
      // MMOStateManager not available — skip credits display
    }

    /* ── Class filter row ────────────────────────────────── */
    this.filterContainer = scene.add.container(PAD, LIST_TOP);
    this.add(this.filterContainer);

    /* ── List ────────────────────────────────────────────── */
    this.listContainer = scene.add.container(PAD, LIST_TOP + FILTER_H + 4);
    this.add(this.listContainer);

    this.listContent = scene.add.container(0, 0);
    this.listContainer.add(this.listContent);

    // Scroll mask (world coordinates for the visible list area)
    const maskGfx = scene.make.graphics({});
    maskGfx.fillRect(this.x + PAD, this.y + LIST_TOP + FILTER_H + 4, LIST_W, SCROLL_H);
    maskGfx.setVisible(false);
    this.listContent.setMask(new Phaser.Display.Masks.GeometryMask(scene, maskGfx));

    /* Scrollbar at right edge of list */
    const scrollBarX = LIST_W + PAD + 2;
    this.scrollTrack = scene.add.graphics();
    this.scrollTrack.setPosition(scrollBarX, LIST_TOP + FILTER_H + 4);
    this.add(this.scrollTrack);

    this.scrollThumb = scene.add.graphics();
    this.scrollThumb.setPosition(scrollBarX, LIST_TOP + FILTER_H + 4);
    this.add(this.scrollThumb);

    /* ── Detail — overlays list, hidden initially ─────── */
    this.detailContainer = scene.add.container(PAD, LIST_TOP);
    this.detailContainer.setVisible(false);
    this.add(this.detailContainer);

    /* ── Status text ─────────────────────────────────────── */
    this.statusText = scene.add.text(DIALOG_W / 2, DIALOG_H - 18, '', {
      fontSize: FontSize.sm, color: '#667788', fontFamily: Fonts.body,
      wordWrap: { width: DIALOG_W - 40 },
    }).setOrigin(0.5, 1);
    this.add(this.statusText);

    /* ── Mouse-wheel scrolling ───────────────────────────── */
    scene.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gos: unknown, _dx: number, dy: number) => {
      if (!this.visible || this.maxScrollY <= 0) return;
      // Only scroll when list is visible (not detail view)
      if (!this.listContainer.visible) return;
      this.scrollY = Phaser.Math.Clamp(this.scrollY - dy * 0.5, -this.maxScrollY, 0);
      this.listContent.y = this.scrollY;
      this.updateScrollbar();
    });

    this.setDepth(2000);
    scene.add.existing(this);

    this.loadVariants();
  }

  private close(): void { closeDialog(this.scene, this); }

  /* ================================================================
     DATA
     ================================================================ */

  private async loadVariants(): Promise<void> {
    this.setStatus('Loading ship variants...');
    const previousSelectedId = this.selectedVariant?.id ?? null;

    // Try fetching variants first, fall back to class prices
    console.log('[ShipStore] Fetching variants...');
    let variants = await this.monetization.fetchShipVariants();
    console.log(`[ShipStore] Got ${variants.length} variants from API`);

    if (variants.length === 0) {
      // Fallback: convert class prices to pseudo-variants
      console.log('[ShipStore] No variants — falling back to class-based prices');
      const prices = await this.monetization.fetchShipPrices();
      if (prices.length === 0) {
        console.log('[ShipStore] No class prices either — giving up');
        this.setStatus('Failed to load ship data. Try again later.');
        return;
      }
      variants = this.pricesToVariants(prices);
      console.log(`[ShipStore] Converted ${variants.length} class prices to pseudo-variants`);
    }

    this.variants = variants;

    // Sort by price ascending
    this.variants.sort((a, b) => a.priceUsd - b.priceUsd);

    this.applyFilter();

    // Preserve selection if possible, otherwise select first
    if (previousSelectedId) {
      this.selectedVariant = this.filteredVariants.find(v => v.id === previousSelectedId) ?? this.filteredVariants[0] ?? null;
    } else {
      this.selectedVariant = this.filteredVariants[0] ?? null;
    }
    this.detailView = 'info';
    this.isPurchasing = false;
    this.setStatus('');
    this.listContainer.setVisible(true);
    this.filterContainer.setVisible(true);
    this.detailContainer.setVisible(false);
    this.renderFilterBar();
    this.renderList();
    this.renderDetail();
  }

  /** Convert old class-based ShipPriceInfo[] to ShipVariantInfo[] for fallback */
  private pricesToVariants(prices: ShipPriceInfo[]): ShipVariantInfo[] {
    return prices.map(p => ({
      id: p.unitType,
      shipClass: p.unitType,
      name: I18n.shipName(p.unitType),
      priceUsd: p.priceUsd,
      hp: p.hp,
      shields: 0,
      attack: p.attack,
      defense: p.defense,
      speed: p.speed,
      attackRange: p.attackRange,
      componentSlots: p.componentSlots,
      towCapacity: 0,
      tonnage: p.tonnage,
      cargoCapacity: 0,
      crewCapacity: 1,
      visionRange: 3,
    }));
  }

  private applyFilter(): void {
    if (this.activeFilter === 'all') {
      this.filteredVariants = [...this.variants];
    } else {
      this.filteredVariants = this.variants.filter(v => v.shipClass === this.activeFilter);
    }
  }

  private setStatus(msg: string, isError = false): void {
    this.statusText.setText(msg);
    this.statusText.setColor(isError ? '#ff4444' : '#667788');
  }

  /* ================================================================
     FILTER BAR — horizontal row of class buttons
     ================================================================ */

  private renderFilterBar(): void {
    this.filterContainer.removeAll(true);

    const BTN_H = 22;
    const BTN_PAD_X = 6;
    const BTN_PAD_Y = 2;
    const GAP = 4;
    let x = 0;

    for (const classKey of CLASS_ORDER) {
      const label = CLASS_LABELS[classKey] || classKey;
      const isActive = this.activeFilter === classKey;

      const btn = this.scene.add.container(x, BTN_PAD_Y);

      // Measure text width for button sizing
      const textObj = this.scene.add.text(0, 0, label, {
        fontSize: '10px', color: isActive ? '#000000' : '#88aacc', fontFamily: Fonts.body,
      });
      const btnW = textObj.width + BTN_PAD_X * 2 + 4;

      const bg = this.scene.add.graphics();
      const drawFilterBg = (active: boolean, hover: boolean) => {
        bg.clear();
        if (active) {
          bg.fillStyle(Colors.cyan, 0.85);
          bg.fillRoundedRect(0, 0, btnW, BTN_H, 3);
        } else if (hover) {
          bg.fillStyle(Colors.cyan, 0.15);
          bg.fillRoundedRect(0, 0, btnW, BTN_H, 3);
          bg.lineStyle(1, Colors.cyan, 0.25);
          bg.strokeRoundedRect(0, 0, btnW, BTN_H, 3);
        } else {
          bg.fillStyle(0x112233, 0.6);
          bg.fillRoundedRect(0, 0, btnW, BTN_H, 3);
          bg.lineStyle(1, Colors.cyan, 0.08);
          bg.strokeRoundedRect(0, 0, btnW, BTN_H, 3);
        }
      };
      drawFilterBg(isActive, false);
      btn.add(bg);

      textObj.setPosition(btnW / 2, BTN_H / 2);
      textObj.setOrigin(0.5, 0.5);
      if (isActive) textObj.setColor('#000a14');
      btn.add(textObj);

      const zone = this.scene.add.zone(0, 0, btnW, BTN_H).setOrigin(0, 0);
      zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, btnW, BTN_H), Phaser.Geom.Rectangle.Contains);
      zone.on('pointerover', () => {
        if (this.activeFilter !== classKey) drawFilterBg(false, true);
        this.scene.input.setDefaultCursor('pointer');
      });
      zone.on('pointerout', () => {
        if (this.activeFilter !== classKey) drawFilterBg(false, false);
        this.scene.input.setDefaultCursor('default');
      });
      zone.on('pointerdown', () => {
        this.activeFilter = classKey;
        this.applyFilter();
        this.scrollY = 0;
        this.selectedVariant = this.filteredVariants[0] ?? null;
        this.renderFilterBar();
        this.renderList();
        this.renderDetail();
      });
      btn.add(zone);

      this.filterContainer.add(btn);
      x += btnW + GAP;
    }

  }

  /* ================================================================
     LEFT – Variant list
     ================================================================ */

  private renderList(): void {
    this.listContent.removeAll(true);
    let y = 0;
    const CARD_H  = 68;
    const GAP     = 5;
    const ICON_SZ = 48;
    const TEXT_X  = 62;  // clear of icon (8 pad + 48 icon + 6 gap)
    const listW = this.listW;

    for (const variant of this.filteredVariants) {
      const isSelected = this.selectedVariant?.id === variant.id;
      const card = this.scene.add.container(0, y);

      /* card background */
      const bg = this.scene.add.graphics();
      const drawBg = (selected: boolean, hover: boolean) => {
        bg.clear();
        const fill = selected ? Colors.bgElevated : hover ? Colors.bgCardHover : Colors.bgCard;
        bg.fillStyle(fill, selected ? 0.9 : 0.8);
        bg.fillRoundedRect(0, 0, listW, CARD_H, Radius.sm);
        const bAlpha = selected ? 0.4 : hover ? 0.15 : 0.06;
        bg.lineStyle(selected ? 2 : 1, Colors.cyan, bAlpha);
        bg.strokeRoundedRect(0, 0, listW, CARD_H, Radius.sm);
      };
      drawBg(isSelected, false);
      card.add(bg);

      /* Ship icon (based on class) */
      const iconKey = SHIP_ICON_KEY[variant.shipClass];
      if (iconKey && this.scene.textures.exists(iconKey)) {
        const icon = this.scene.add.image(8 + ICON_SZ / 2, CARD_H / 2, iconKey);
        icon.setDisplaySize(ICON_SZ, ICON_SZ);
        icon.setAlpha(isSelected ? 1 : 0.75);
        card.add(icon);
      }

      /* Variant name */
      card.add(this.scene.add.text(TEXT_X, 8, variant.name, {
        fontSize: FontSize.sm, color: CSSColors.textWhite, fontFamily: Fonts.heading,
      }));

      /* Class label */
      const classLabel = (CLASS_LABELS[variant.shipClass] || variant.shipClass) + ' Class';
      card.add(this.scene.add.text(TEXT_X, 24, classLabel, {
        fontSize: '9px', color: '#556688', fontFamily: Fonts.body,
      }));

      /* Compact stats */
      const statsLine = `HP ${variant.hp}  ATK ${variant.attack}  SPD ${variant.speed}  CRG ${variant.cargoCapacity}`;
      card.add(this.scene.add.text(TEXT_X, 40, statsLine, {
        fontSize: '9px', color: '#556677', fontFamily: Fonts.body,
      }));

      /* Special trait (truncated) */
      if (variant.specialTrait) {
        const traitStr = variant.specialTrait.length > 30
          ? variant.specialTrait.substring(0, 28) + '..'
          : variant.specialTrait;
        card.add(this.scene.add.text(TEXT_X, 53, traitStr, {
          fontSize: '8px', color: '#447766', fontFamily: Fonts.body, fontStyle: 'italic',
        }));
      }

      /* Price badge — top right corner */
      const priceStr = `$${variant.priceUsd.toLocaleString()}`;
      card.add(this.scene.add.text(listW - 10, 12, priceStr, {
        fontSize: FontSize.sm, color: CSSColors.gold, fontFamily: Fonts.heading,
      }).setOrigin(1, 0));

      /* Zone hitbox */
      const zone = this.scene.add.zone(0, 0, listW, CARD_H).setOrigin(0, 0);
      zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, listW, CARD_H), Phaser.Geom.Rectangle.Contains);
      zone.on('pointerover', () => {
        if (this.selectedVariant?.id !== variant.id) drawBg(false, true);
        this.scene.input.setDefaultCursor('pointer');
      });
      zone.on('pointerout', () => {
        if (this.selectedVariant?.id !== variant.id) drawBg(false, false);
        this.scene.input.setDefaultCursor('default');
      });
      zone.on('pointerdown', () => {
        this.selectedVariant = variant;
        this.detailView = 'info';
        this.renderList();
        this.renderDetail();
        this.listContainer.setVisible(false);
        this.filterContainer.setVisible(false);
        this.detailContainer.setVisible(true);
      });
      card.add(zone);

      this.listContent.add(card);
      y += CARD_H + GAP;
    }

    if (this.filteredVariants.length === 0) {
      this.listContent.add(this.scene.add.text(listW / 2, 40, 'No variants in this class', {
        fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.body,
      }).setOrigin(0.5));
      y = 80;
    }

    this.totalContentH = y;
    this.maxScrollY = Math.max(0, y - this.scrollH);
    this.scrollY = Phaser.Math.Clamp(this.scrollY, -this.maxScrollY, 0);
    this.listContent.y = this.scrollY;
    this.updateScrollbar();
  }

  /** Draw/update the scrollbar track + thumb */
  private updateScrollbar(): void {
    this.scrollTrack.clear();
    this.scrollThumb.clear();

    // Only show if there's overflow
    if (this.maxScrollY <= 0) return;

    const TRACK_W = 3;
    const TRACK_H = this.scrollH;

    // Track (subtle background)
    this.scrollTrack.fillStyle(0xffffff, 0.04);
    this.scrollTrack.fillRoundedRect(0, 0, TRACK_W, TRACK_H, 1);

    // Thumb size proportional to visible ratio
    const visibleRatio = this.scrollH / this.totalContentH;
    const thumbH = Math.max(24, Math.round(TRACK_H * visibleRatio));

    // Thumb position based on scroll progress
    const scrollProgress = this.maxScrollY > 0 ? -this.scrollY / this.maxScrollY : 0;
    const thumbY = Math.round(scrollProgress * (TRACK_H - thumbH));

    this.scrollThumb.fillStyle(Colors.cyan, 0.25);
    this.scrollThumb.fillRoundedRect(0, thumbY, TRACK_W, thumbH, 1);
  }

  /* ================================================================
     RIGHT – Detail / confirm
     ================================================================ */

  private renderDetail(): void {
    this.detailContainer.removeAll(true);
    if (!this.selectedVariant) {
      this.detailContainer.add(this.scene.add.text(this.detailW / 2, 60, 'Select a ship', {
        fontSize: FontSize.sm, color: '#556677', fontFamily: Fonts.body,
      }).setOrigin(0.5));
      return;
    }

    if (this.detailView === 'info') {
      this.renderInfoView();
    } else {
      this.renderConfirmView();
    }
  }

  /* ── Info view ─────────────────────────────────────────── */

  private renderInfoView(): void {
    const variant = this.selectedVariant!;
    const detailW = this.detailW;
    let y = 0;

    /* Back to list button */
    this.detailContainer.add(this.createSmallBtn(0, 0, 80, 22, '\u2190 Back', () => {
      this.detailContainer.setVisible(false);
      this.listContainer.setVisible(true);
      this.filterContainer.setVisible(true);
      this.detailView = 'info';
      this.renderList();
    }));
    y += 28;

    /* Ship portrait */
    const iconKey = SHIP_ICON_KEY[variant.shipClass];
    if (iconKey && this.scene.textures.exists(iconKey)) {
      const portrait = this.scene.add.image(detailW / 2, y + 45, iconKey);
      portrait.setDisplaySize(80, 80);
      this.detailContainer.add(portrait);
      y += 92;
    } else {
      y += 6;
    }

    /* Variant name */
    this.detailContainer.add(this.scene.add.text(detailW / 2, y, variant.name, {
      fontSize: FontSize.xl, color: CSSColors.textWhite, fontFamily: Fonts.heading,
    }).setOrigin(0.5, 0));
    y += 22;

    /* Class */
    const classLabel = (CLASS_LABELS[variant.shipClass] || variant.shipClass) + ' Class';
    const role = SHIP_ROLE[variant.shipClass] || '';
    this.detailContainer.add(this.scene.add.text(detailW / 2, y, `${classLabel}${role ? ' \u2014 ' + role : ''}`, {
      fontSize: FontSize.xs, color: '#556688', fontFamily: Fonts.body,
    }).setOrigin(0.5, 0));
    y += 16;

    /* Special trait */
    if (variant.specialTrait) {
      this.detailContainer.add(this.scene.add.text(detailW / 2, y, `"${variant.specialTrait}"`, {
        fontSize: '10px', color: '#447766', fontFamily: Fonts.body, fontStyle: 'italic',
        wordWrap: { width: detailW - 20 },
      }).setOrigin(0.5, 0));
      y += 18;
    }

    /* Divider */
    this.addDivider(y); y += 8;

    /* Stats section */
    this.detailContainer.add(this.scene.add.text(0, y, 'SHIP STATS', {
      fontSize: FontSize.xs, color: '#5588aa', fontFamily: Fonts.heading,
    }));
    y += 16;

    const stats: [string, number][] = [
      ['Hit Points',      variant.hp],
      ['Shields',         variant.shields],
      ['Attack',          variant.attack],
      ['Defense',         variant.defense],
      ['Speed',           variant.speed],
      ['Attack Range',    variant.attackRange],
      ['Vision Range',    variant.visionRange],
      ['Component Slots', variant.componentSlots],
      ['Crew Capacity',   variant.crewCapacity],
      ['Cargo Capacity',  variant.cargoCapacity],
      ['Tonnage',         variant.tonnage],
      ['Tow Capacity',    variant.towCapacity],
    ];

    for (const [label, value] of stats) {
      y = this.addStatRow(0, y, label, String(value), '#aaccdd');
    }
    y += 6;

    /* Divider */
    this.addDivider(y); y += 8;

    /* Price */
    this.detailContainer.add(this.scene.add.text(detailW / 2, y, `$${variant.priceUsd.toLocaleString()} USD`, {
      fontSize: FontSize.xl, color: CSSColors.gold, fontFamily: Fonts.heading,
    }).setOrigin(0.5, 0));
    y += 30;

    /* Buy button */
    this.detailContainer.add(this.createActionBtn(
      0, y, detailW, 34,
      `Buy ${variant.name}`,
      0x115522, 0x22aa44,
      () => {
        this.detailView = 'confirm';
        this.renderDetail();
      },
    ));
  }

  /* ── Confirm view ──────────────────────────────────────── */

  private renderConfirmView(): void {
    const variant = this.selectedVariant!;
    const detailW = this.detailW;
    let y = 0;

    /* Back button */
    this.detailContainer.add(this.createSmallBtn(0, 0, 80, 22, '\u2190 Back', () => {
      this.detailView = 'info';
      this.isPurchasing = false;
      this.renderDetail();
    }));
    y += 28;

    /* Header */
    this.detailContainer.add(this.scene.add.text(detailW / 2, y, 'CONFIRM PURCHASE', {
      fontSize: FontSize.md, color: CSSColors.textWhite, fontFamily: Fonts.heading,
    }).setOrigin(0.5, 0));
    y += 26;

    this.addDivider(y); y += 10;

    /* Ship summary */
    const iconKey = SHIP_ICON_KEY[variant.shipClass];
    if (iconKey && this.scene.textures.exists(iconKey)) {
      const icon = this.scene.add.image(20, y + 20, iconKey);
      icon.setDisplaySize(40, 40);
      this.detailContainer.add(icon);
    }

    this.detailContainer.add(this.scene.add.text(48, y, variant.name, {
      fontSize: FontSize.lg, color: CSSColors.textWhite, fontFamily: Fonts.heading,
    }));
    this.detailContainer.add(this.scene.add.text(48, y + 22, `$${variant.priceUsd.toLocaleString()} USD`, {
      fontSize: FontSize.sm, color: CSSColors.gold, fontFamily: Fonts.body,
    }));
    y += 54;

    this.addDivider(y); y += 10;

    /* Chain + stablecoin selector (PaymentSelectionPanel) */
    this.paymentPanel = new PaymentSelectionPanel(this.scene, 0, y, detailW, {
      defaultChain: this.selectedChain,
      defaultStablecoin: this.selectedStablecoin,
      onChanged: () => {
        this.selectedChain = this.paymentPanel!.getSelectedChain();
        this.selectedStablecoin = this.paymentPanel!.getSelectedStablecoin();
        this.renderDetail();
      },
    });
    this.detailContainer.add(this.paymentPanel);
    y += this.paymentPanel.getHeight() + 4;

    this.addDivider(y); y += 10;

    /* Order summary */
    const classLabel = (CLASS_LABELS[variant.shipClass] || variant.shipClass) + ' Class';
    this.addInfoRow(0, y, 'Ship', variant.name, CSSColors.textWhite);       y += 18;
    this.addInfoRow(0, y, 'Class', classLabel, '#aaccdd');                   y += 18;
    this.addInfoRow(0, y, 'Crew Capacity', String(variant.crewCapacity), '#aaccdd'); y += 18;
    this.addInfoRow(0, y, 'Spawns At', 'Earth (0, 0)', '#55cc88');           y += 18;
    this.addInfoRow(0, y, 'Price', `$${variant.priceUsd.toLocaleString()}`, CSSColors.gold);  y += 18;
    this.addInfoRow(0, y, 'Chain', CHAINS.find(c => c.id === this.selectedChain)!.label, '#aaccdd'); y += 18;
    this.addInfoRow(0, y, 'Token', this.selectedStablecoin, '#aaccdd');       y += 24;

    /* Confirm / Processing button */
    if (this.isPurchasing) {
      this.detailContainer.add(this.createActionBtn(
        0, y, detailW, 38, 'PROCESSING...', 0x333333, 0x555555, () => {},
      ));
    } else {
      this.detailContainer.add(this.createActionBtn(
        0, y, detailW, 38,
        `Confirm \u2014 $${variant.priceUsd.toLocaleString()}`,
        0x115522, 0x22aa44,
        () => this.executePurchase(),
      ));
    }
  }

  /* ================================================================
     PURCHASE
     ================================================================ */

  private async executePurchase(): Promise<void> {
    if (!this.selectedVariant || this.isPurchasing) return;

    const variant = this.selectedVariant;

    this.isPurchasing = true;
    this.renderDetail();
    this.setStatus(`Purchasing ${variant.name} for $${variant.priceUsd.toLocaleString()} ${this.selectedStablecoin} on ${this.selectedChain}...`);

    try {
      let paymentFlow = ServiceLocator.tryGet<PaymentFlowManager>(Services.PAYMENT_FLOW);
      if (!paymentFlow) {
        const network = ServiceLocator.tryGet<any>(Services.MMO_NETWORK);
        if (network) {
          paymentFlow = new PaymentFlowManager(() => network.getSessionToken());
          ServiceLocator.register(Services.PAYMENT_FLOW, paymentFlow);
        }
      }

      if (paymentFlow) {
        const result = await paymentFlow.executePayment({
          assetType: 'ship',
          amountUsd: variant.priceUsd,
          chain: this.selectedChain,
          stablecoin: this.selectedStablecoin,
          metadata: { unitType: variant.shipClass, variantId: variant.id, worldId: 1 },
        });

        this.isPurchasing = false;

        if (result.success) {
          this.setStatus(`\u2713 ${variant.name} deployed successfully!`);
          EventBus.emit('show-notification', {
            text: `${variant.name} deployed at Earth (0,0)! Check your fleet.`,
            color: '#00ff88',
            duration: 6000,
          });
          EventBus.emit('send_message', {
            type: 'mmo_request_chunks',
            payload: { chunks: [{ chunkX: 0, chunkY: 0 }] },
          });
          if (result.assetId) {
            EventBus.emit('select-purchased-unit', { unitId: result.assetId });
          }
          this.scene.time.delayedCall(2000, () => {
            if (this.scene) this.destroy();
          });
        } else {
          this.setStatus(`Purchase failed: ${result.error}`, true);
          this.renderDetail();
        }
      } else {
        this.isPurchasing = false;
        this.setStatus('Payment system not available', true);
        this.renderDetail();
      }
    } catch (err) {
      this.isPurchasing = false;
      const message = err instanceof Error ? err.message : 'Wallet connection failed';
      this.setStatus(`Purchase failed: ${message}`, true);
      this.renderDetail();
    }
  }

  /* ================================================================
     UI HELPERS
     ================================================================ */

  /** Horizontal thin divider in the detail panel */
  private addDivider(y: number): void {
    const g = this.scene.add.graphics();
    g.lineStyle(1, Colors.cyan, 0.06);
    g.lineBetween(0, y, this.detailW, y);
    this.detailContainer.add(g);
  }

  /** Label ........... value row with dotted leader */
  private addStatRow(x: number, y: number, label: string, value: string, valueColor: string): number {
    const ROW_H = 18;
    const detailW = this.detailW;
    const row = this.scene.add.container(x, y);

    const labelText = this.scene.add.text(0, 0, label, {
      fontSize: FontSize.xs, color: '#889999', fontFamily: Fonts.body,
    });
    row.add(labelText);

    const valText = this.scene.add.text(detailW, 0, value, {
      fontSize: FontSize.xs, color: valueColor, fontFamily: Fonts.body,
    }).setOrigin(1, 0);
    row.add(valText);

    // Dotted leader
    const dotStart = labelText.width + 4;
    const dotEnd   = detailW - valText.width - 4;
    const dots = this.scene.add.text(dotStart, 0, '', {
      fontSize: FontSize.xs, color: '#334455', fontFamily: Fonts.body,
    });
    let dotStr = '';
    while (dots.width < dotEnd - dotStart) { dotStr += ' \u00B7'; dots.setText(dotStr); }
    row.add(dots);

    // Hover highlight
    const hoverBg = this.scene.add.graphics();
    row.addAt(hoverBg, 0);
    const hitZone = this.scene.add.zone(0, 0, detailW, ROW_H).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, detailW, ROW_H), Phaser.Geom.Rectangle.Contains);
    hitZone.on('pointerover', () => {
      hoverBg.clear();
      hoverBg.fillStyle(0xffffff, 0.03);
      hoverBg.fillRoundedRect(-2, -2, detailW + 4, ROW_H + 2, 2);
    });
    hitZone.on('pointerout', () => { hoverBg.clear(); });
    row.add(hitZone);

    this.detailContainer.add(row);
    return y + ROW_H;
  }

  /** Simple label: value info row */
  private addInfoRow(x: number, y: number, label: string, value: string, valueColor: string = '#aaccdd'): void {
    const labelText = this.scene.add.text(x, y, `${label}: `, {
      fontSize: FontSize.xs, color: '#889999', fontFamily: Fonts.body,
    });
    this.detailContainer.add(labelText);

    this.detailContainer.add(this.scene.add.text(x + labelText.width, y, value, {
      fontSize: FontSize.xs, color: valueColor, fontFamily: Fonts.body,
    }));
  }

  /** Full-width action button with Zone hitbox */
  private createActionBtn(
    x: number, y: number, w: number, h: number,
    label: string, fillColor: number, borderColor: number,
    onClick: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);

    const bg = this.scene.add.graphics();
    const draw = (hover: boolean) => {
      bg.clear();
      const c = hover ? Phaser.Display.Color.IntegerToColor(fillColor).brighten(20).color : fillColor;
      bg.fillStyle(c, 1);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, borderColor, 0.6);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
    };
    draw(false);
    btn.add(bg);

    btn.add(this.scene.add.text(w / 2, h / 2, label, {
      fontSize: FontSize.sm, color: '#ffffff', fontFamily: Fonts.heading,
    }).setOrigin(0.5));

    const zone = this.scene.add.zone(0, 0, w, h).setOrigin(0, 0);
    zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    zone.on('pointerover', () => { draw(true); this.scene.input.setDefaultCursor('pointer'); });
    zone.on('pointerout',  () => { draw(false); this.scene.input.setDefaultCursor('default'); });
    zone.on('pointerdown', onClick);
    btn.add(zone);

    return btn;
  }

  /** Small button (Back, etc.) */
  private createSmallBtn(
    x: number, y: number, w: number, h: number,
    label: string, onClick: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);

    const bg = this.scene.add.graphics();
    const draw = (hover: boolean) => {
      bg.clear();
      bg.fillStyle(hover ? 0x2a3a5a : 0x1e2844, 0.9);
      bg.fillRoundedRect(0, 0, w, h, Radius.sm);
      bg.lineStyle(1, Colors.cyan, hover ? 0.2 : 0.1);
      bg.strokeRoundedRect(0, 0, w, h, Radius.sm);
    };
    draw(false);
    btn.add(bg);

    btn.add(this.scene.add.text(w / 2, h / 2, label, {
      fontSize: FontSize.xs, color: '#8899bb', fontFamily: Fonts.body,
    }).setOrigin(0.5));

    const zone = this.scene.add.zone(0, 0, w, h).setOrigin(0, 0);
    zone.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    zone.on('pointerover', () => { draw(true); this.scene.input.setDefaultCursor('pointer'); });
    zone.on('pointerout',  () => { draw(false); this.scene.input.setDefaultCursor('default'); });
    zone.on('pointerdown', onClick);
    btn.add(zone);

    return btn;
  }

}
